// Shared task metadata — the single source of truth for task type labels/dots,
// per-type action labels, status chips, and the small formatters used by both
// the Home Tasks card and the Task detail drawer. Mirrors the extension's
// getActionMode + the design bundle's TypeLabel / drawer status logic.

import { format, parseISO } from "date-fns";

import type { ChipTone } from "components/ds/Chip";
import { hopLabel } from "state/accessPaths";
import { appDisplayName } from "modules/apps/tenants";
import type {
  MFResourceAccessEntity,
  MFSelectableResource,
  MFTaskMember,
} from "state/members/tasks";

export const INDIRECT_REMEDIATION_ENTITY_TYPE = "indirect_access_remediation";

export const ENTITLEMENT_ENTITY_TYPES = new Set([
  "entitlement_provisioning",
  "entitlement_deprovisioning",
]);

export const isEntitlementTask = (entityType: string): boolean =>
  ENTITLEMENT_ENTITY_TYPES.has(entityType);

// Assignment entity types that can be approved/denied in place.
export const APPROVABLE = new Set([
  "access_approval",
  "provisioning",
  "deprovisioning",
  "access_provisioning_batch",
  "entitlement_adjustment",
  INDIRECT_REMEDIATION_ENTITY_TYPE,
  ...ENTITLEMENT_ENTITY_TYPES,
]);

// Entity types intentionally excluded from the portal's personal task feed (the
// Home Tasks card + the Tasks page, active and completed). Batch approvals and
// access-review campaigns are bulk/admin workflows with their own dedicated
// surfaces — they shouldn't clutter an individual's task queue. Applied once at
// the state layer (MF_TASKS.useListTasks / useListCompletedTasks) so every
// consumer and count stays consistent.
const HIDDEN_TASK_ENTITY_TYPES = new Set([
  "access_provisioning_batch",
  "access_review_campaign",
]);

export const isPortalTask = (entityType: string): boolean =>
  !HIDDEN_TASK_ENTITY_TYPES.has(entityType);

// Approvable types whose ✓/✗ must go through the drawer rather than the inline
// control: TaskActions is written for access requests (its modal offers to
// grant or deny "the requested access", and its plain Confirm denies with no
// reason), which reads as the wrong question for a removal and refuses one
// without a reason for the admin to act on.
const DRAWER_ONLY = new Set([INDIRECT_REMEDIATION_ENTITY_TYPE]);

// A task row is actionable in place (inline ✓/✗) when it's an assignment with a
// real assignee whose type can be approved/denied outside the drawer. Shared by the Home Tasks card
// and the Tasks page so the rule can't drift between them. Structural param to
// avoid importing MFTaskItem (which would cycle: tasks.ts imports from here).
export const isActionableTask = (task: {
  taskType: string;
  assigneeId?: string;
  entityType: string;
}): boolean =>
  task.taskType === "assignment" &&
  Boolean(task.assigneeId) &&
  APPROVABLE.has(task.entityType) &&
  !DRAWER_ONLY.has(task.entityType);

// Stable React key for a task row across the card + page (assignee → offboarding
// → campaign → positional fallback).
export const taskRowKey = (
  task: { assigneeId?: string; offboardingId?: string; campaignCycleId?: string },
  index: number,
): string =>
  task.assigneeId ?? task.offboardingId ?? task.campaignCycleId ?? `task-${index}`;

// design ref: components.jsx TypeLabel — elongated dot + label, per type.
export const TYPE_META: Record<string, { label: string; color: string }> = {
  access_approval: { label: "Access approval", color: "accents.royal-purple" },
  provisioning: { label: "Provisioning", color: "success.500" },
  deprovisioning: { label: "Deprovisioning", color: "error.500" },
  access_provisioning_batch: { label: "Access batch", color: "p.500" },
  entitlement_adjustment: {
    label: "Entitlement adjustment",
    color: "warning.500",
  },
  indirect_access_remediation: {
    label: "Indirect access",
    color: "error.500",
  },
  entitlement_provisioning: { label: "Give entitlement", color: "success.500" },
  entitlement_deprovisioning: { label: "Remove entitlement", color: "error.500" },
  offboarding: { label: "Offboarding", color: "warning.500" },
  access_review_campaign: { label: "Access review", color: "p.500" },
};

export const typeMeta = (entityType: string) =>
  TYPE_META[entityType] ?? { label: "Task", color: "neutral.400" };

// Per-type Approve/Deny labels, mirroring the extension's getActionMode.
export const actionLabels = (
  entityType: string,
): { approve: string; deny: string } => {
  switch (entityType) {
    case "provisioning":
    case "deprovisioning":
    case "entitlement_provisioning":
    case "entitlement_deprovisioning":
    case INDIRECT_REMEDIATION_ENTITY_TYPE:
      return { approve: "Mark complete", deny: "Refuse" };
    case "access_provisioning_batch":
      return { approve: "Approve batch", deny: "Reject batch" };
    case "entitlement_adjustment":
      return { approve: "Mark complete", deny: "Refuse" };
    default:
      return { approve: "Approve", deny: "Deny" };
  }
};

// Header status chip (Type · Status row). Resolved states win; otherwise an
// access approval reads "Input needed", process tasks read "In progress".
// design ref: drawers.jsx normalizedStatus + statusLabel block.
export const taskStatusMeta = (
  status: string | undefined,
  entityType: string,
): { label: string; color: string } => {
  switch (status) {
    case "approved":
      return { label: "Approved", color: "success.500" };
    case "denied":
      return { label: "Denied", color: "error.500" };
    case "refused":
      return { label: "Refused", color: "error.500" };
    case "cancelled":
      return { label: "Cancelled", color: "neutral.500" };
    case "completed":
    case "complete":
      return { label: "Completed", color: "success.500" };
    default:
      return entityType === "access_approval"
        ? { label: "Input needed", color: "warning.500" }
        : { label: "In progress", color: "p.500" };
  }
};

// The requester's employment status chip (Member.status enum).
export const memberStatusMeta = (
  status: string | null | undefined,
): { label: string; tone: ChipTone } | null => {
  switch (status) {
    case "active":
      return { label: "Active", tone: "success" };
    case "inactive":
      return { label: "Inactive", tone: "error" };
    case "inactive_onboarding":
      return { label: "Onboarding", tone: "warning" };
    case "converted_to_nhi":
      return { label: "Service account", tone: "azurite" };
    default:
      return null;
  }
};

// Human duration from seconds — mirrors the extension's formatDuration.
export const formatDuration = (
  seconds?: number | null,
  indefinite?: boolean | null,
): string => {
  if (indefinite) return "Indefinite";
  if (!seconds) return "Not specified";
  const days = Math.floor(seconds / 86400);
  if (days > 0) return `${days} day${days !== 1 ? "s" : ""}`;
  const hours = Math.floor((seconds % 86400) / 3600);
  if (hours > 0) return `${hours} hour${hours !== 1 ? "s" : ""}`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
};

// Human label for a Resource.type enum (provisioning/deprovisioning "Type"
// row). Falls back to a Title Case of the raw value for the long tail.
const RESOURCE_TYPE_LABELS: Record<string, string> = {
  app: "App",
  idp_app: "App",
  group: "Group",
  role: "Role",
  permission_set: "Permission set",
  access_package: "Access package",
  license: "License",
  channel: "Channel",
  repository: "Repository",
  project: "Project",
  database: "Database",
  server: "Server",
  drive: "Drive",
  bucket: "Bucket",
  policy: "Policy",
  account: "Account",
  profile: "Profile",
};

export const resourceTypeLabel = (type?: string | null): string | null => {
  if (!type) return null;
  return (
    RESOURCE_TYPE_LABELS[type] ??
    type
      .split("_")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ")
  );
};

// "2024-01-13" → "Jan 13 2024". Returns null on an unparseable value.
export const formatDate = (value?: string | null): string | null => {
  if (!value) return null;
  try {
    return format(parseISO(value), "MMM d yyyy");
  } catch {
    return null;
  }
};

// Tasks-page type filter buckets. The four headline types map 1:1, and giving or
// removing an entitlement counts as provisioning or deprovisioning; everything
// else (batches, entitlement adjustments, access-review campaigns) rolls up
// into "generic".
export type TaskFilterKey =
  | "access_approval"
  | "deprovisioning"
  | "provisioning"
  | "offboarding"
  | "generic";

export const taskFilterCategory = (entityType: string): TaskFilterKey => {
  switch (entityType) {
    case "access_approval":
      return "access_approval";
    case "deprovisioning":
    case "entitlement_deprovisioning":
      return "deprovisioning";
    case "provisioning":
    case "entitlement_provisioning":
      return "provisioning";
    case "offboarding":
      return "offboarding";
    default:
      return "generic";
  }
};

// Completed-task outcome badge from the instance `result` + entity type.
// `result` is "completed" / "refused" (or an approve/deny synonym for approvals).
export const completedResolutionMeta = (
  result: string | null | undefined,
  entityType: string,
): { label: string; tone: ChipTone; positive: boolean } => {
  const negative =
    result === "refused" ||
    result === "denied" ||
    result === "rejected" ||
    result === "deny";

  if (entityType === "access_approval")
    return negative
      ? { label: "Denied", tone: "error", positive: false }
      : { label: "Approved", tone: "success", positive: true };

  if (entityType === "deprovisioning" || entityType === "entitlement_deprovisioning")
    return negative
      ? { label: "Kept", tone: "default", positive: false }
      : { label: "Removed", tone: "success", positive: true };

  if (entityType === "offboarding")
    return negative
      ? { label: "Declined", tone: "error", positive: false }
      : { label: "Completed", tone: "success", positive: true };

  if (entityType === "access_review_campaign")
    return { label: "Reviewed", tone: "success", positive: true };

  return negative
    ? { label: "Refused", tone: "error", positive: false }
    : { label: "Complete", tone: "success", positive: true };
};

// Toast copy after responding to a task — a short outcome header + a plain,
// helpful "You …" sentence (e.g. "Refused" / "You kept Ergert Saliasi's access").
export const respondToast = (
  entityType: string,
  decision: "approve" | "deny",
  name?: string | null,
  resourceName?: string | null,
): { title: string; description: string } => {
  const approve = decision === "approve";
  const who = name?.trim() || "this member";
  const forRes = resourceName ? ` for ${resourceName}` : "";
  const toRes = resourceName ? ` to ${resourceName}` : "";
  const onRes = resourceName ? ` on ${resourceName}` : "";

  switch (entityType) {
    case "access_approval":
      return {
        title: approve ? "Access approved" : "Access denied",
        description: approve
          ? `You approved ${who}'s request${forRes}.`
          : `You denied ${who}'s request${forRes}.`,
      };
    case "provisioning":
      return {
        title: approve ? "Marked complete" : "Refused",
        description: approve
          ? `You granted ${who} access${toRes}.`
          : `You declined ${who}'s access request.`,
      };
    case "deprovisioning":
      return {
        title: approve ? "Marked complete" : "Refused",
        description: approve
          ? `You revoked ${who}'s access${toRes}.`
          : `You kept ${who}'s access${toRes}.`,
      };
    case "entitlement_provisioning":
      return {
        title: approve ? "Marked complete" : "Refused",
        description: approve
          ? `You gave ${who} the entitlement${onRes}.`
          : `You refused the entitlement for ${who}${onRes}.`,
      };
    case "entitlement_deprovisioning":
      return {
        title: approve ? "Marked complete" : "Refused",
        description: approve
          ? `You removed ${who}'s entitlement${onRes}.`
          : `You kept ${who}'s entitlement${onRes}.`,
      };
    case "access_provisioning_batch":
      return {
        title: approve ? "Batch approved" : "Batch rejected",
        description: approve
          ? "You approved the bulk access request."
          : "You rejected the bulk access request.",
      };
    case INDIRECT_REMEDIATION_ENTITY_TYPE:
      return {
        title: approve ? "Marked complete" : "Refused",
        description: approve
          ? `You removed ${who}'s indirect access${toRes}.`
          : `You kept ${who}'s indirect access${toRes}.`,
      };
    case "entitlement_adjustment":
      return {
        title: approve ? "Marked complete" : "Refused",
        description: approve ? "You completed the task." : "You refused the task.",
      };
    default:
      return {
        title: approve ? "Approved" : "Denied",
        description: approve ? "You completed the task." : "You refused the task.",
      };
  }
};

// Copy for the inline control's "Add note" modal. Confirming there completes
// the task outright, so each type asks the question its decision actually
// answers: a removal or an adjustment is not someone getting access.
export const confirmModalCopy = (
  entityType: string,
  decision: "approve" | "deny",
  name?: string | null,
): { title: string; body: string; notePlaceholder: string } => {
  const approve = decision === "approve";
  const who = name?.trim() || "this member";
  const optionalNote = "Add a note (optional)…";

  switch (entityType) {
    case "provisioning":
      return approve
        ? {
            title: `Mark ${who}'s access as granted?`,
            body: `Confirm you have given ${who} the requested access. This marks the task complete.`,
            notePlaceholder: optionalNote,
          }
        : {
            title: `Refuse ${who}'s access?`,
            body: `The request is declined and ${who} does not get this access.`,
            notePlaceholder: "Reason for refusing…",
          };
    case "deprovisioning":
      return approve
        ? {
            title: `Mark ${who}'s access as removed?`,
            body: `Confirm you have removed this access from ${who}. This marks the task complete.`,
            notePlaceholder: optionalNote,
          }
        : {
            title: `Keep ${who}'s access?`,
            body: `The removal is refused and ${who} keeps this access.`,
            notePlaceholder: "Reason for keeping this access…",
          };
    case "entitlement_adjustment":
      return approve
        ? {
            title: `Mark the adjustment for ${who} as done?`,
            body: `Confirm you have made the entitlement changes flagged for ${who}. This marks the task complete.`,
            notePlaceholder: optionalNote,
          }
        : {
            title: `Refuse the adjustment for ${who}?`,
            body: `The flagged entitlement changes are not made and ${who}'s access stays as it is.`,
            notePlaceholder: "Reason for refusing…",
          };
    case "entitlement_provisioning":
      return approve
        ? {
            title: `Mark the entitlement as given to ${who}?`,
            body: `Confirm you have given ${who} the entitlement named in this task. This marks the task complete.`,
            notePlaceholder: optionalNote,
          }
        : {
            title: `Refuse the entitlement for ${who}?`,
            body: `The entitlement is not given and ${who}'s access stays as it is.`,
            notePlaceholder: "Reason for refusing…",
          };
    case "entitlement_deprovisioning":
      return approve
        ? {
            title: `Mark the entitlement as removed from ${who}?`,
            body: `Confirm you have removed the entitlement named in this task from ${who}. This marks the task complete.`,
            notePlaceholder: optionalNote,
          }
        : {
            title: `Keep ${who}'s entitlement?`,
            body: `The removal is refused and ${who} keeps this entitlement.`,
            notePlaceholder: "Reason for keeping this entitlement…",
          };
    default:
      return approve
        ? {
            title: `Approve access for ${name ?? "this request"}?`,
            body: `${name ?? "The requester"} will get the requested access once all approvals are complete. This can be revoked at any time.`,
            notePlaceholder: optionalNote,
          }
        : {
            title: `Deny access for ${name ?? "this request"}?`,
            body: `${name ?? "The requester"} will be notified and will see your reason. Make it clear so they know if they can reapply.`,
            notePlaceholder: `Reason for denying: ${name ?? "they"} will see this…`,
          };
  }
};

// Pull the entitlement-adjustment specifics out of an assignment `body`. The
// server builds that string with fixed headers (see the backend's
// EntitlementAdjustmentService.build_assignment_body) — Employee / Resource are
// already shown as cards in the drawer, so we only extract the two things that
// view is missing: the flagged entitlements and the reviewer's reason.
const ENT_HEADER = "*Entitlements flagged for adjustment:*";
const REASON_HEADER = "*Reason for adjustment:*";

export const parseEntitlementAdjustment = (
  body?: string | null,
): { entitlements: string[]; reason: string | null } => {
  if (!body) return { entitlements: [], reason: null };

  const entIdx = body.indexOf(ENT_HEADER);
  const reasonIdx = body.indexOf(REASON_HEADER);

  const block =
    entIdx === -1
      ? ""
      : body.slice(
          entIdx + ENT_HEADER.length,
          reasonIdx > entIdx ? reasonIdx : body.length,
        );

  const entitlements = block
    .split("\n")
    .map((line) => line.replace(/^[•\-*\s]+/, "").trim()) // strip bullet markers
    .filter(Boolean);

  const reason =
    reasonIdx === -1 ? null : body.slice(reasonIdx + REASON_HEADER.length).trim() || null;

  return { entitlements, reason };
};

export type EntitlementTask = {
  change: "give" | "remove";
  entitlementName: string | null;
  entitlementTypeLabel: string | null;
};

export const readEntitlementTask = (
  entityType: string,
  entity?: MFResourceAccessEntity | null,
): EntitlementTask => ({
  change: entityType === "entitlement_deprovisioning" ? "remove" : "give",
  entitlementName: entity?.entitlement?.name?.trim() || null,
  entitlementTypeLabel: resourceTypeLabel(entity?.entitlement?.type),
});

export type EntitlementSystem = {
  integrationName: string | null;
  connectionName: string | null;
  accountIdentifier: string | null;
};

export const readEntitlementSystem = (
  entity?: MFResourceAccessEntity | null,
): EntitlementSystem => {
  const integrationName = entity?.integrationName?.trim() || null;
  const connectionName = entity?.connectionName?.trim() || null;
  return {
    integrationName,
    connectionName:
      connectionName && connectionName !== integrationName ? connectionName : null,
    accountIdentifier: entity?.accountIdentifier?.trim() || null,
  };
};

export type AccessRequestContext = {
  catalogItemName: string | null;
  catalogName: string | null;
  duration: string | null;
  comment: string | null;
};

const NO_REQUEST_CONTEXT: AccessRequestContext = {
  catalogItemName: null,
  catalogName: null,
  duration: null,
  comment: null,
};

// An entitlement task's access carries the request that first gave it
// access, often months before this change, so none of it describes the task.
export const readAccessRequestContext = (
  entityType: string,
  entity: MFResourceAccessEntity,
): AccessRequestContext =>
  isEntitlementTask(entityType)
    ? NO_REQUEST_CONTEXT
    : {
        catalogItemName: entity.catalogItemName || null,
        catalogName: entity.catalogName || null,
        duration:
          entity.requestedForSeconds || entity.requestedIndefinite
            ? formatDuration(entity.requestedForSeconds, entity.requestedIndefinite)
            : null,
        comment: entity.requestComments || null,
      };

// Compact relative time, e.g. "just now" / "5h ago" / "4d ago".
export const timeAgo = (value?: string | null): string => {
  if (!value) return "";
  try {
    const secs = Math.max(0, Math.round((Date.now() - parseISO(value).getTime()) / 1000));
    if (secs < 60) return "just now";
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
  } catch {
    return "";
  }
};

export const memberFullName = (
  m?: { firstName?: string | null; lastName?: string | null } | null,
): string => (m ? `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim() : "");

export const approvalResourceName = (
  sr?: MFSelectableResource | null,
): string | undefined => {
  if (sr?.name) return appDisplayName(sr);
  if (sr?.resource?.name) return appDisplayName(sr.resource);
  return undefined;
};

export const memberLocation = (
  m?: { locationCity?: string | null; locationCountry?: string | null } | null,
): string | null => {
  if (!m) return null;
  if (m.locationCity && m.locationCountry)
    return `${m.locationCity}, ${m.locationCountry}`;
  return m.locationCity || m.locationCountry || null;
};

// The requester on a task is usually a member, but an NHI (non-human identity)
// access request has no member — the requester is a service account / agent, so
// the payload carries an identity descriptor instead (or, until the server sends
// one, nothing). resolveTaskPerson normalises all three cases so the person card
// never dereferences a null member.
export type TaskPersonIdentity = { name: string; type?: string | null };

export type TaskPerson =
  | { kind: "member"; member: MFTaskMember }
  | { kind: "identity"; identity: TaskPersonIdentity }
  | { kind: "unknown" };

export const resolveTaskPerson = (
  member?: MFTaskMember | null,
  identity?: { name?: string | null; type?: string | null } | null,
): TaskPerson => {
  if (member) return { kind: "member", member };
  if (identity && (identity.name || identity.type))
    return {
      kind: "identity",
      identity: { name: identity.name || "Non-human identity", type: identity.type },
    };
  return { kind: "unknown" };
};

// The indirect-access-remediation task's structured payload. The assignment
// serialisers emit `metadata` verbatim, so it arrives in the database's
// snake_case; camelCase keys are read too in case a serialiser ever camelises.

export type IndirectRemediationNamedEntitlement = {
  name: string;
  type: string | null;
};

export type IndirectRemediationHop = {
  resourceId: string | null;
  resourceName: string;
  resourceType: string | null;
  parentResourceName: string | null;
  entitlements: IndirectRemediationNamedEntitlement[];
};

export type IndirectRemediationRoute = { hops: IndirectRemediationHop[] };

export type IndirectRemediationCollateral = {
  intermediaryId: string | null;
  intermediaryName: string;
  intermediaryType: string | null;
  otherResources: { id: string | null; name: string; type: string | null }[];
  otherEntitlementsOnReviewedResource: IndirectRemediationNamedEntitlement[];
};

export type IndirectRemediationTask = {
  accountId: string | null;
  identityName: string | null;
  identityEmail: string | null;
  resourceId: string | null;
  resourceName: string | null;
  resourceType: string | null;
  routes: IndirectRemediationRoute[];
  collateral: IndirectRemediationCollateral[];
};

type MetadataBag = Record<string, unknown>;

const isBag = (value: unknown): value is MetadataBag =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const bagString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value : null;

const bagList = (value: unknown): MetadataBag[] =>
  Array.isArray(value) ? value.filter(isBag) : [];

const pick = (bag: MetadataBag, ...keys: string[]): unknown =>
  keys.map((key) => bag[key]).find((value) => value !== undefined);

const namedEntitlements = (
  value: unknown,
): IndirectRemediationNamedEntitlement[] =>
  bagList(value).flatMap((entry) => {
    const name = bagString(entry.name);
    return name ? [{ name, type: bagString(entry.type) }] : [];
  });

export const readIndirectRemediation = (
  metadata: Record<string, unknown> | null | undefined,
): IndirectRemediationTask => {
  const bag: MetadataBag = isBag(metadata) ? metadata : {};

  return {
    accountId: bagString(pick(bag, "account_id", "accountId")),
    identityName: bagString(pick(bag, "identity_name", "identityName")),
    identityEmail: bagString(pick(bag, "identity_email", "identityEmail")),
    resourceId: bagString(pick(bag, "resource_id", "resourceId")),
    resourceName: bagString(pick(bag, "resource_name", "resourceName")),
    resourceType: bagString(pick(bag, "resource_type", "resourceType")),
    routes: bagList(bag.routes).map((route) => ({
      hops: bagList(route.hops).map((hop) => {
        // A hop the server could not name is still a hop: dropping it would
        // shorten the route, and a two-hop route losing its group would leave
        // the reviewed resource itself as the removal target.
        const resourceName =
          bagString(pick(hop, "resource_name", "resourceName")) ??
          "Unnamed resource";
        return {
          resourceId: bagString(pick(hop, "resource_id", "resourceId")),
          resourceName,
          resourceType: bagString(pick(hop, "resource_type", "resourceType")),
          parentResourceName: bagString(
            pick(hop, "parent_resource_name", "parentResourceName"),
          ),
          entitlements: namedEntitlements(hop.entitlements),
        };
      }),
    })),
    collateral: bagList(bag.collateral).flatMap((entry) => {
      const intermediaryName = bagString(
        pick(entry, "intermediary_name", "intermediaryName"),
      );
      if (!intermediaryName) return [];
      return [
        {
          intermediaryId: bagString(
            pick(entry, "intermediary_id", "intermediaryId"),
          ),
          intermediaryName,
          intermediaryType: bagString(
            pick(entry, "intermediary_type", "intermediaryType"),
          ),
          otherResources: bagList(
            pick(entry, "other_resources", "otherResources"),
          ).flatMap((resource) => {
            const name = bagString(resource.name);
            return name
              ? [
                  {
                    id: bagString(resource.id),
                    name,
                    type: bagString(resource.type),
                  },
                ]
              : [];
          }),
          otherEntitlementsOnReviewedResource: namedEntitlements(
            pick(
              entry,
              "other_entitlements_on_reviewed_resource",
              "otherEntitlementsOnReviewedResource",
            ),
          ),
        },
      ];
    }),
  };
};

// One step per group the person is a DIRECT member of (the first hop of each
// live route), since that membership is the only thing the assignee can
// actually remove. Routes through the same group fold into one step; a route
// that runs through further groups before the reviewed resource keeps its path
// so the step can say how the access arrives.
export type IndirectRemediationStep = {
  intermediaryId: string | null;
  intermediaryName: string;
  intermediaryType: string | null;
  intermediaryParentName: string | null;
  /** Hop labels, first hop to reviewed resource, for routes longer than one hop. */
  paths: string[][];
  grantsOnReviewedResource: IndirectRemediationNamedEntitlement[];
  alsoLoses: { id: string | null; name: string; type: string | null }[];
  onlySourceOf: IndirectRemediationNamedEntitlement[];
};

const uniqueByName = <T extends { name: string }>(items: T[]): T[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.name)) return false;
    seen.add(item.name);
    return true;
  });
};

// The server never raises a removal task without a live route, so an empty
// list means the detail has not arrived (or failed to). Answering it then would
// close the review row without the assignee ever seeing which group to act on.
export const canAnswerTask = (
  entityType: string,
  detailMetadata: Record<string, unknown> | null | undefined,
): boolean =>
  entityType !== INDIRECT_REMEDIATION_ENTITY_TYPE ||
  readIndirectRemediation(detailMetadata).routes.length > 0;

export const indirectRemediationSteps = (
  task: IndirectRemediationTask,
): IndirectRemediationStep[] => {
  const steps = new Map<string, IndirectRemediationStep>();

  task.routes.forEach((route) => {
    const [first] = route.hops;
    if (!first) return;

    const key = first.resourceId ?? first.resourceName;
    const collateral = task.collateral.find((entry) =>
      entry.intermediaryId
        ? entry.intermediaryId === first.resourceId
        : entry.intermediaryName === first.resourceName,
    );
    const step = steps.get(key) ?? {
      intermediaryId: first.resourceId,
      intermediaryName: first.resourceName,
      intermediaryType: first.resourceType,
      intermediaryParentName: first.parentResourceName,
      paths: [],
      grantsOnReviewedResource: [],
      alsoLoses: collateral?.otherResources ?? [],
      onlySourceOf: collateral?.otherEntitlementsOnReviewedResource ?? [],
    };

    if (route.hops.length > 2) {
      // Without the last hop: it is the reviewed resource, which the person
      // loses rather than something to remove them from.
      step.paths.push(route.hops.slice(0, -1).map(hopLabel));
    }
    step.grantsOnReviewedResource = uniqueByName([
      ...step.grantsOnReviewedResource,
      // Only the edge INTO the reviewed resource — the last hop — grants
      // anything on it; an earlier hop's entitlements belong to that hop.
      ...(route.hops.at(-1)?.entitlements ?? []),
    ]);

    steps.set(key, step);
  });

  return [...steps.values()];
};
